import { PencilSimpleIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import type { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useId, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { CountBadge } from '../../components/badges/count-badge.tsx'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { ErrorBoundaryFallback } from '../../components/error-boundaries/error-boundary-fallback.tsx'
import { Checkbox } from '../../components/input/checkbox.tsx'
import { AddRepositoriesModal } from '../../components/modals/add-repositories-modal.tsx'
import { DeleteProjectModal } from '../../components/modals/delete-project-modal.tsx'
import { EditProjectModal } from '../../components/modals/edit-project-modal.tsx'
import { MoveRepositoryModal } from '../../components/modals/move-repository-modal.tsx'
import { type RemovableRepository, RemoveRepositoryModal } from '../../components/modals/remove-repository-modal.tsx'
import { projectFullReason, remainingCapacity } from '../../components/projects/project-capacity.ts'
import * as layout from '../../components/projects/project-page-layout.css.ts'
import { ProjectRepositoryCard } from '../../components/projects/project-repository-card.tsx'
import * as cardStyles from '../../components/projects/project-row-card.css.ts'
import { useReturnFocus } from '../../components/projects/return-focus.ts'
import { useCloseOnLostPermission } from '../../components/projects/use-close-on-lost-permission.ts'
import { type ActionButtonProps, BulkActionBar } from '../../components/table/bulk-action-bar.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { useRepositoryRiskScores } from '../../hooks/use-repository-risk-scores.ts'
import { Routes } from '../../routes.ts'
import { ApiError } from '../../utils/api-client/api-error.ts'
import {
  useGetLatestAnalysisForRepository,
  useGetProject,
  useGetProjectMembershipsFromHalLink,
  useGetProjectsPage,
  useGetRepositories,
} from '../../utils/api-client/api-hooks.ts'
import type { Project, Repository } from '../../utils/api-client/api-schemas.ts'
import { type AvailableTemplate, getTemplate, type TemplateLookup } from '../../utils/api-client/templates.ts'
import { formatRelativeTime } from '../../utils/date-utils.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './project-details-page.css.ts'

/** One page holds every member while capacities stay under this; the server caps every project at 100 today. */
const MEMBERS_PAGE_SIZE = 200

// Interim: until the Move flow can create its destination itself (ISS-9669), a move needs a second project to exist.
const MOVE_NEEDS_ANOTHER_PROJECT = 'Create a new project first in order to move repositories'

type Overlay =
  | { kind: 'add'; template: AvailableTemplate }
  | { kind: 'delete'; template: AvailableTemplate }
  | { kind: 'edit'; template: AvailableTemplate }
  // `fromSelection` says whether the bulk bar opened this, so finishing it clears the selection the bar
  // was acting on and a single row's kebab menu leaves that selection alone.
  | { kind: 'move'; repositories: Repository[]; fromSelection: boolean }
  | { kind: 'remove'; repositories: RemovableRepository[]; fromSelection: boolean }

type MemberOperations = { move: TemplateLookup; remove: TemplateLookup }

const getMemberOperations = (repository: Repository): MemberOperations => {
  const membership = repository._embedded?.['project-membership']
  if (!membership) {
    return { move: { state: 'unavailable' }, remove: { state: 'unavailable' } }
  }
  return { move: getTemplate(membership, 'replace'), remove: getTemplate(membership, 'delete') }
}

const isSelectable = ({ move, remove }: MemberOperations) => move.state === 'available' || remove.state === 'available'

/** Whether a refreshed answer no longer offers the operation. Still loading is not a loss. */
const isLost = (lookup: TemplateLookup) => lookup.state === 'unavailable' || lookup.state === 'not-adopted'

/**
 * Whether every repository a dialog was opened for still offers its operation. One that has left the
 * project is skipped: a partly finished bulk removal takes rows with it while its dialog stays open.
 */
const isStillOffered = (current: Repository[], chosen: Pick<Repository, 'id'>[], operation: keyof MemberOperations) =>
  chosen.every(({ id }) => {
    const row = current.find(repository => repository.id === id)
    return row === undefined || !isLost(getMemberOperations(row)[operation])
  })

const toRemovable = (repository: Repository, remove: TemplateLookup): RemovableRepository | undefined =>
  remove.state === 'available'
    ? { ...repository, membershipDelete: { method: remove.method, target: remove.target } }
    : undefined

export const ProjectDetailsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  invariant(projectId, 'projectId is required')
  const { data: project, isPending, error, refetch, isRefetching } = useGetProject({ projectId })

  if (isPending) {
    return (
      <div className={layout.page}>
        <p className={layout.loading}>Loading project…</p>
      </div>
    )
  }

  // A 404 wins over cached data on purpose: after a delete, or a link to a project that is gone, the
  // cache may still hold the last good response, and rendering it would offer actions that 404. Any
  // other failure says nothing about the project's existence, so it is not reported as "not found".
  if (error instanceof ApiError && error.status === 404) {
    return (
      <ProjectUnavailable projectId={projectId} crumb="Not found">
        <div className={layout.emptyCard}>
          <h3 className={layout.emptyTitle}>Project not found</h3>
          <p className={layout.emptyText}>It may have been deleted. The projects list has what remains.</p>
        </div>
      </ProjectUnavailable>
    )
  }

  // A failed refetch behind a project already on screen leaves it there rather than blanking the page.
  if (!project) {
    return (
      <ProjectUnavailable projectId={projectId} crumb="Unavailable">
        <ErrorBoundaryFallback
          title="Unable to load project"
          detail={error ? <ErrorDetail error={error} /> : 'Something went wrong while loading this project.'}
          onRetry={() => void refetch()}
          isRetrying={isRefetching}
        />
      </ProjectUnavailable>
    )
  }

  return <ProjectDetailsContent key={projectId} project={project} />
}

const ProjectUnavailable: React.FC<React.PropsWithChildren<{ projectId: string; crumb: string }>> = ({
  projectId,
  crumb,
  children,
}) => (
  <div className={layout.page}>
    <Breadcrumb
      navAriaLabel="Project breadcrumb"
      items={[
        { label: 'Projects', to: Routes.ProjectsPage.path },
        { label: crumb, to: Routes.ProjectDetailsPage.createPath({ projectId }) },
      ]}
    />
    {children}
  </div>
)

const ProjectDetailsContent: React.FC<{ project: Project }> = ({ project }) => {
  const projectId = project.id
  const navigate = useNavigate()
  const [overlay, setOverlay] = useState<Overlay>()
  // Every dialog here mounts already open, from a control it does not render, so closing one has to
  // put focus back by hand. @see useReturnFocus
  const { captureOpener, onCloseAutoFocus, fallbackRef } = useReturnFocus()
  const openOverlay = (next: Overlay) => {
    captureOpener()
    setOverlay(next)
  }
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())

  const membershipsQuery = useGetProjectMembershipsFromHalLink({ path: project._links.memberships.href })
  const { data: memberships, isError: isMembershipsFailed } = membershipsQuery
  const addMembers = getTemplate(memberships, 'create')

  const membersQuery = useGetRepositories({
    pageIndex: 0,
    pageSize: MEMBERS_PAGE_SIZE,
    projectIds: [projectId],
    embedProjectMembership: true,
  })
  const { data: members, isError: membersFailed } = membersQuery
  const repositories = useMemo(() => members?._embedded.items ?? [], [members])
  const selectable = useMemo(
    () => repositories.filter(repository => isSelectable(getMemberOperations(repository))),
    [repositories]
  )
  const repositoryCount = members?.total ?? project.repository_count
  // Only the total is needed. While it loads, or if it fails, Move stays enabled and its dialog reports
  // whatever it finds.
  const { data: projectsPage } = useGetProjectsPage({ size: 1 })
  const moveDisabledReason =
    projectsPage !== undefined && projectsPage.total <= 1 ? MOVE_NEEDS_ANOTHER_PROJECT : undefined
  // The member list's total is fresher than the project's own count, so it is the one compared against
  // the capacity the project reports. The server refuses what this misses.
  const isFull =
    remainingCapacity({ repository_capacity: project.repository_capacity, repository_count: repositoryCount }) === 0

  // Derived from what is on screen rather than held alongside it, so a repository that left the project
  // — moved, removed, or gone from the refetched page — cannot stay in the selection unseen.
  const selected = useMemo(
    () => selectable.filter(repository => selectedIds.has(repository.id)),
    [selectable, selectedIds]
  )
  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])
  const selectAll = useCallback(
    () => setSelectedIds(new Set(selectable.map(repository => repository.id))),
    [selectable]
  )
  const toggleSelected = useCallback((repositoryId: string) => {
    setSelectedIds(current => {
      const next = new Set(current)
      if (!next.delete(repositoryId)) {
        next.add(repositoryId)
      }
      return next
    })
  }, [])

  const closeOverlay = () => setOverlay(undefined)

  useCloseOnLostPermission({
    isOpen: overlay !== undefined,
    isAvailable: match(overlay)
      .with(undefined, () => true)
      .with({ kind: 'add' }, () => !isLost(addMembers))
      .with({ kind: 'edit' }, () => !isLost(getTemplate(project, 'replace')))
      .with({ kind: 'delete' }, () => !isLost(getTemplate(project, 'delete')))
      .with({ kind: 'move' }, ({ repositories: moving }) => isStillOffered(repositories, moving, 'move'))
      .with({ kind: 'remove' }, ({ repositories: removing }) => isStillOffered(repositories, removing, 'remove'))
      .exhaustive(),
    onClose: closeOverlay,
    message: <>That change is no longer available.</>,
  })

  const finishAction = (fromSelection: boolean) => () => {
    if (fromSelection) {
      clearSelection()
    }
    closeOverlay()
  }

  // The memberships collection gates "+ Add repositories", and the member list gates every row. If
  // either has nothing to show, the region fails as one: which request carries an affordance is the
  // API's to change, so it does not get an error state of its own.
  const isRepositoriesUnreadable =
    (isMembershipsFailed && memberships === undefined) || (membersFailed && members === undefined)
  // Loading follows the same rule, so no row or control can be used before both answers are in.
  const isRepositoriesLoading = !isRepositoriesUnreadable && (members === undefined || memberships === undefined)
  const isRepositoriesReady = !isRepositoriesUnreadable && !isRepositoriesLoading

  const isReadOnly =
    (addMembers.state === 'unavailable' || addMembers.state === 'not-adopted') &&
    members !== undefined &&
    selectable.length === 0

  const selectedRemovals = selected.flatMap(repository => {
    const removable = toRemovable(repository, getMemberOperations(repository).remove)
    return removable ? [removable] : []
  })
  const canMoveSelection = selected.every(repository => getMemberOperations(repository).move.state === 'available')
  const canRemoveSelection = selectedRemovals.length === selected.length
  const bulkActionCandidates: (ActionButtonProps | undefined)[] = [
    canMoveSelection
      ? {
          buttonText: 'Move',
          buttonType: 'ghost',
          isDisabled: moveDisabledReason !== undefined,
          disabledTooltip: moveDisabledReason,
          onAction: () => openOverlay({ kind: 'move', repositories: selected, fromSelection: true }),
        }
      : undefined,
    canRemoveSelection
      ? {
          buttonText: 'Remove',
          onAction: () => openOverlay({ kind: 'remove', repositories: selectedRemovals, fromSelection: true }),
        }
      : undefined,
  ]
  const bulkActions = bulkActionCandidates.filter((action): action is ActionButtonProps => action !== undefined)

  return (
    <div className={layout.page}>
      <Breadcrumb
        navAriaLabel="Project breadcrumb"
        items={[
          { label: 'Projects', to: Routes.ProjectsPage.path },
          { label: project.name, to: Routes.ProjectDetailsPage.createPath({ projectId }) },
        ]}
      />

      <div className={styles.header}>
        <div className={layout.titleBlock}>
          <h1 className={layout.heading}>{project.name}</h1>
          <p className={classNames(layout.description, styles.countLine)}>
            <CountBadge count={repositoryCount} />
            {/* The leading space collapses in the flex layout but keeps the text reading "2 repositories". */}
            <span>
              {' '}
              {pluralize(repositoryCount, 'repository', 'repositories')}
              {project.description && <> · {project.description}</>}
            </span>
          </p>
        </div>
        <ProjectHeaderActions
          project={project}
          onEdit={template => openOverlay({ kind: 'edit', template })}
          onDelete={template => openOverlay({ kind: 'delete', template })}
        />
      </div>

      {/* The focus fallback for a dialog whose opener is gone when it closes — a bulk remove takes its
          rows and the selection bar with it. Not in the tab order; only reachable by that restore, and
          named so a reader landing here is told where they are. */}
      <div
        className={styles.repositoriesRegion}
        ref={fallbackRef}
        tabIndex={-1}
        role="region"
        aria-label="Repositories in this project"
      >
        {isRepositoriesReady && (
          <AddRepositoriesControl
            addMembers={addMembers}
            fullReason={isFull ? projectFullReason(project.repository_capacity) : undefined}
            onAdd={template => openOverlay({ kind: 'add', template })}
          />
        )}
        {isReadOnly && <p className={layout.readOnlyNotice}>You can&apos;t change this project&apos;s repositories.</p>}
        {isRepositoriesReady && members && members.total > repositories.length && (
          <p className={layout.readOnlyNotice}>
            Showing the first {repositories.length} of {members.total} repositories.
          </p>
        )}
        {/* "No repositories yet" only once the members have arrived: claimed while loading, or after a
            failed read, it contradicts the count in the subtitle. */}
        {match({ isRepositoriesUnreadable, isRepositoriesLoading, hasMembers: repositories.length > 0 })
          .with({ isRepositoriesUnreadable: true }, () => (
            <RepositoriesLoadError queries={[membersQuery, membershipsQuery]} />
          ))
          .with({ isRepositoriesLoading: true }, () => <p className={layout.loading}>Loading repositories…</p>)
          .with({ hasMembers: true }, () => (
            <>
              {selectable.length > 0 && (
                <SelectAllRow
                  repositories={selectable}
                  selectedCount={selected.length}
                  onChange={checked => (checked ? selectAll() : clearSelection())}
                />
              )}
              <RepositoryCards
                repositories={repositories}
                selectedIds={selectedIds}
                onToggleSelected={toggleSelected}
                moveDisabledReason={moveDisabledReason}
                onMove={repository => openOverlay({ kind: 'move', repositories: [repository], fromSelection: false })}
                onRemove={repository =>
                  openOverlay({ kind: 'remove', repositories: [repository], fromSelection: false })
                }
              />
            </>
          ))
          .otherwise(() => (
            <div className={layout.emptyCard}>
              <h3 className={layout.emptyTitle}>No repositories yet</h3>
              <p className={layout.emptyText}>Add repositories to scope feedback to them.</p>
            </div>
          ))}
      </div>

      {/* The findings table's bar, so a selection reads and behaves the same wherever one is made. */}
      {isRepositoriesReady && selected.length > 0 && (
        <BulkActionBar selectedCount={selected.length} onClear={clearSelection} actionButtonsProps={bulkActions} />
      )}

      {overlay &&
        match(overlay)
          .with({ kind: 'add' }, ({ template }) => (
            <AddRepositoriesModal
              project={project}
              template={template}
              onClose={closeOverlay}
              onAdded={closeOverlay}
              onCloseAutoFocus={onCloseAutoFocus}
            />
          ))
          .with({ kind: 'edit' }, ({ template }) => (
            <EditProjectModal
              project={project}
              template={template}
              onClose={closeOverlay}
              onSaved={closeOverlay}
              onCloseAutoFocus={onCloseAutoFocus}
            />
          ))
          .with({ kind: 'delete' }, ({ template }) => (
            <DeleteProjectModal
              onCloseAutoFocus={onCloseAutoFocus}
              project={project}
              template={template}
              onClose={closeOverlay}
              onDeleted={() => void navigate(Routes.ProjectsPage.path)}
            />
          ))
          .with({ kind: 'move' }, ({ repositories: moving, fromSelection }) => (
            <MoveRepositoryModal
              onCloseAutoFocus={onCloseAutoFocus}
              repositories={moving}
              currentProject={project}
              onClose={closeOverlay}
              onMoved={finishAction(fromSelection)}
            />
          ))
          .with({ kind: 'remove' }, ({ repositories: removing, fromSelection }) => (
            <RemoveRepositoryModal
              onCloseAutoFocus={onCloseAutoFocus}
              repositories={removing}
              project={project}
              onClose={closeOverlay}
              onRemoved={finishAction(fromSelection)}
            />
          ))
          .exhaustive()}
    </div>
  )
}

const ProjectHeaderActions: React.FC<{
  project: Project
  onEdit: (template: AvailableTemplate) => unknown
  onDelete: (template: AvailableTemplate) => unknown
}> = ({ project, onEdit, onDelete }) => {
  const replaceProject = getTemplate(project, 'replace')
  const deleteProject = getTemplate(project, 'delete')
  if (replaceProject.state !== 'available' && deleteProject.state !== 'available') {
    return undefined
  }
  return (
    <div className={styles.headerActions}>
      {replaceProject.state === 'available' && (
        <TooltipWithHover
          size="small"
          trigger={
            <button
              type="button"
              className={styles.headerButton}
              aria-label="Edit project"
              onClick={() => onEdit({ method: replaceProject.method, target: replaceProject.target })}
            >
              <PencilSimpleIcon size={20} weight="light" aria-hidden />
            </button>
          }
        >
          Edit project
        </TooltipWithHover>
      )}
      {deleteProject.state === 'available' && (
        <TooltipWithHover
          size="small"
          trigger={
            <button
              type="button"
              className={styles.headerButtonDanger}
              aria-label="Delete project"
              onClick={() => onDelete({ method: deleteProject.method, target: deleteProject.target })}
            >
              <TrashSimpleIcon size={20} weight="light" aria-hidden />
            </button>
          }
        >
          Delete project
        </TooltipWithHover>
      )}
    </div>
  )
}

const AddRepositoriesControl: React.FC<{
  addMembers: TemplateLookup
  /** Why adding is disabled, present only when the project is full. */
  fullReason?: string
  onAdd: (template: AvailableTemplate) => unknown
}> = ({ addMembers, fullReason, onAdd }) =>
  match(addMembers)
    .with({ state: 'available' }, ({ method, target }) => (
      <div className={layout.barEnd}>
        {fullReason && <p className={styles.capacityNotice}>{fullReason}</p>}
        <DefaultButton
          size="small"
          state={fullReason ? 'disabled' : 'default'}
          onClick={() => onAdd({ method, target })}
        >
          + Add repositories
        </DefaultButton>
      </div>
    ))
    .otherwise(() => undefined)

/** The list's header checkbox: checks and clears every row, and shows the mixed state in between. */
const SelectAllRow: React.FC<{
  repositories: Repository[]
  selectedCount: number
  onChange: (checked: boolean) => unknown
}> = ({ repositories, selectedCount, onChange }) => {
  const isAll = selectedCount === repositories.length
  const checkboxId = useId()
  return (
    <div className={styles.selectAllRow}>
      <Checkbox
        id={checkboxId}
        checked={selectedCount === 0 ? false : isAll ? true : 'indeterminate'}
        icon={isAll ? 'check' : 'dash'}
        onCheckedChange={checked => onChange(checked === true)}
      />
      <label htmlFor={checkboxId} className={styles.selectAllLabel}>
        {selectedCount > 0 ? `${selectedCount} of ${repositories.length} selected` : 'Select all repositories'}
      </label>
    </div>
  )
}

const RepositoryCards: React.FC<{
  repositories: Repository[]
  selectedIds: ReadonlySet<string>
  onToggleSelected: (repositoryId: string) => unknown
  moveDisabledReason?: string
  onMove: (repository: Repository) => unknown
  onRemove: (repository: RemovableRepository) => unknown
}> = ({ repositories, selectedIds, onToggleSelected, moveDisabledReason, onMove, onRemove }) => {
  const riskScores = useRepositoryRiskScores(repositories)
  return (
    <div className={cardStyles.list}>
      {repositories.map(repository => {
        const rowOperations = getMemberOperations(repository)
        const removable = toRemovable(repository, rowOperations.remove)
        return (
          <RepositoryRow
            key={repository.id}
            repository={repository}
            riskScore={riskScores.get(repository.id)?.riskScore}
            selection={
              isSelectable(rowOperations)
                ? { isSelected: selectedIds.has(repository.id), onToggle: () => onToggleSelected(repository.id) }
                : undefined
            }
            onMove={rowOperations.move.state === 'available' ? () => onMove(repository) : undefined}
            moveDisabledReason={moveDisabledReason}
            onRemove={removable ? () => onRemove(removable) : undefined}
          />
        )
      })}
    </div>
  )
}

const RepositoryRow: React.FC<{
  repository: Repository
  riskScore?: number
  selection?: { isSelected: boolean; onToggle: () => unknown }
  onMove?: () => unknown
  moveDisabledReason?: string
  onRemove?: () => unknown
}> = ({ repository, riskScore, selection, onMove, moveDisabledReason, onRemove }) => {
  const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: repository.id })
  const scannedAt = latestAnalysis?._embedded?.scan?.scanned_at ?? latestAnalysis?.current_state.timestamp
  return (
    <ProjectRepositoryCard
      repository={repository}
      riskScore={riskScore}
      lastScanned={scannedAt ? formatRelativeTime(scannedAt) : undefined}
      selection={selection}
      onMove={onMove}
      moveDisabledReason={moveDisabledReason}
      onRemove={onRemove}
    />
  )
}

type RetryableQuery = Pick<UseQueryResult, 'isError' | 'error' | 'refetch' | 'isRefetching'>

// Both requests gate the repositories region, so Retry re-reads whichever of them failed.
const RepositoriesLoadError: React.FC<{ queries: RetryableQuery[] }> = ({ queries }) => {
  const failed = queries.filter(query => query.isError)
  const error = failed.find(query => query.error)?.error
  return (
    <ErrorBoundaryFallback
      title="Unable to load repositories"
      detail={error && <ErrorDetail error={error} />}
      onRetry={() => failed.forEach(query => void query.refetch())}
      isRetrying={queries.some(query => query.isRefetching)}
    />
  )
}
